import { Link } from 'react-router-dom';
import { t, tn } from '@app/i18n';
import { Alert, Badge, Card, InfoTip, TasksInExecution, type BadgeTone } from '@zarya/ui';
import { dataCurta, dataHour } from '@app/format';
import { ROUTE_DOCS } from '@app/navigation';
import { ReadingNote } from '@app/reading';
import type { Overview } from './Overview.api';
import { cardFailure, failureText } from './degradation';
import s from './Overview.module.css';

/**
 * Card **Conexão com o cliente** (FR-094) — o indicador de saúde que a aderência
 * (`#mudancas` › Infraestrutura) pediu: estado do middleware e do túnel, versão,
 * última sincronização e a última falha **com código e hora**.
 *
 * A última falha não é enfeite: é o que separa "nunca conectou" de "conectava e
 * parou". Sem hora e código, o operador não sabe qual das duas.
 */

type Connection = Overview['connection'];
type State = NonNullable<Connection['middleware_state']>;

/** O tom é do desenho, não do idioma; o rótulo vem do catálogo na hora de pintar. */
const TONE: Record<State, BadgeTone> = {
  alcancavel: 'success',
  fora: 'danger',
  nao_configurado: 'warning',
};

function stateView(estado: State): { rotulo: string; tone: BadgeTone } {
  return { rotulo: t(`overview.connection.state.${estado}`), tone: TONE[estado] };
}

/** `TriEstado` (`boolean | null`): `null` é "não lido", nunca "não". */
function triState(valor: boolean | null | undefined): { rotulo: string; tone: BadgeTone } {
  if (valor === true) return { rotulo: t('overview.connection.tunnel.on'), tone: 'success' };
  if (valor === false) return { rotulo: t('overview.connection.tunnel.off'), tone: 'danger' };
  return { rotulo: t('overview.connection.tunnel.unknown'), tone: 'neutral' };
}

type SpireRoot = NonNullable<Connection['spire_root_ca']>;

/**
 * Marca de cada estado da raiz da CA do SPIRE. Os símbolos não são texto de idioma e
 * ficam no código (mesmo critério do `AskTrail`); o que se lê sai do catálogo, e é ele
 * que diz o estado a quem usa leitor de tela — a marca é `aria-hidden`.
 */
const SPIRE_ROOT_MARK: Record<SpireRoot['state'], { symbol: string; className: string }> = {
  ok: { symbol: '✓', className: s.vOk },
  expiring: { symbol: '⚠', className: s.vWrn },
  expired: { symbol: '✕', className: s.vDgr },
  unknown: { symbol: '—', className: s.vazio },
};

/**
 * Onde se renova a raiz: a seção «Renovar a raiz da CA» de «Preparar o gateway de
 * agentes» (`implante/gateway.md`). A âncora é o `slugify` do título — se o título
 * mudar, o teste de links da documentação não vê este endereço, e é por isso que ele
 * tem um teste próprio.
 */
export const SPIRE_ROOT_RENEW_DOC = `${ROUTE_DOCS}/gateway#renovar-a-raiz-da-ca`;

/**
 * Linha «Raiz da CA do SPIRE» (aim-o3r.36). Quatro estados vêm do executor e um do
 * produto: `null` é «ninguém leu» (não há foto do `/debug/status`, aim-6ph.13), e diz
 * «não lido», como o túnel — não «não foi possível ler», que é o `unknown` do executor.
 */
function SpireRootValue({ root }: { root: Connection['spire_root_ca'] }) {
  if (!root) {
    return <Badge tone="neutral">{t('overview.connection.spireRoot.notRead')}</Badge>;
  }
  const mark = SPIRE_ROOT_MARK[root.state];
  const date = dataCurta(root.not_after) ?? '—';

  let text: string;
  if (root.state === 'unknown') {
    text = t('overview.connection.spireRoot.unknown');
  } else if (root.state === 'expired') {
    text = t('overview.connection.spireRoot.expired', { date });
  } else {
    text = t('overview.connection.spireRoot.expiresOn', {
      date,
      days: tn('overview.connection.spireRoot.days', root.days_left ?? 0),
    });
  }

  // Os `{' '}` entre os elementos não se apagam: num contêiner flex o espaço solto não é
  // desenhado (quem espaça é o `gap`), mas sem ele o `textContent` — o que se copia e o
  // que o teste lê — colava «✓vence em…». Com ele, a linha lê-se exatamente como o
  // protótipo aprovado.
  return (
    <>
      <span className={mark.className} aria-hidden="true">
        {mark.symbol}
      </span>{' '}
      <span data-testid="spire-root-text">{text}</span>
      {root.state === 'expiring' ? (
        <>
          {' '}
          <span className={s.linhaMeta} aria-hidden="true">
            ·
          </span>{' '}
          <span>{t('overview.connection.spireRoot.renew')}</span>{' '}
          <Link className={s.linkAcao} to={SPIRE_ROOT_RENEW_DOC}>
            {t('overview.connection.spireRoot.renewLink')}
          </Link>
        </>
      ) : null}
    </>
  );
}

interface RowProps {
  rotulo: string;
  tip: string;
  children: React.ReactNode;
}

function Row({ rotulo, tip, children }: RowProps) {
  return (
    <div className={s.linha}>
      <span className={s.linhaRotulo}>
        {rotulo}
        <InfoTip
          text={tip}
          label={t('overview.connection.rowTipLabel', { label: rotulo })}
          size="xs"
        />
      </span>
      <span className={s.linhaValor}>{children}</span>
    </div>
  );
}

export interface ConnectionCardProps {
  overview: Overview;
  /** A tela pediu a leitura nova do cluster e o pedido não voltou: o rodapé já diz «lendo». */
  readingPending?: boolean;
}

export function ConnectionCard({ overview, readingPending = false }: ConnectionCardProps) {
  const falha = cardFailure(overview.failures, 'conexao');
  const conexao = overview.connection;
  const estado = stateView(conexao.middleware_state ?? 'nao_configurado');
  const tunel = triState(conexao.tunnel);
  const sync = dataHour(conexao.last_sync_at);
  const failureAt = dataHour(conexao.last_failure_at);

  return (
    <Card
      title={t('overview.connection.title')}
      tip={t('overview.connection.tip')}
      toolbar={
        <Link className={s.linkAcao} to="/tenant">
          {t('overview.connection.link')}
        </Link>
      }
    >
      {falha ? (
        <Alert tone="warning">{failureText(falha)}</Alert>
      ) : (
        <>
          <Row
            rotulo={t('overview.connection.row.middleware')}
            tip={t('overview.connection.row.middleware.tip')}
          >
            <Badge tone={estado.tone}>{estado.rotulo}</Badge>
            {conexao.middleware_version ? (
              <span className={s.mono}>v{conexao.middleware_version}</span>
            ) : null}
            {/* Mesmo indicador do cabeçalho, menor: quem está olhando a conexão é
                quem acabou de disparar a ordem (29/08). */}
            <TasksInExecution compacto />
          </Row>

          {conexao.middleware_url ? (
            <Row
              rotulo={t('overview.connection.row.url')}
              tip={t('overview.connection.row.url.tip')}
            >
              <span className={s.mono}>{conexao.middleware_url}</span>
            </Row>
          ) : null}

          <Row
            rotulo={t('overview.connection.row.tunnel')}
            tip={t('overview.connection.row.tunnel.tip')}
          >
            <Badge tone={tunel.tone}>{tunel.rotulo}</Badge>
          </Row>

          <Row
            rotulo={t('overview.connection.row.spireRoot')}
            tip={t('overview.connection.row.spireRoot.tip')}
          >
            <SpireRootValue root={conexao.spire_root_ca} />
          </Row>

          <Row
            rotulo={t('overview.connection.row.lastSync')}
            tip={t('overview.connection.row.lastSync.tip')}
          >
            {sync ?? <span className={s.vazio}>{t('overview.connection.row.lastSync.never')}</span>}
          </Row>

          <Row
            rotulo={t('overview.connection.row.lastFailure')}
            tip={t('overview.connection.row.lastFailure.tip')}
          >
            {conexao.last_failure_code ? (
              <>
                <span className={s.mono}>{conexao.last_failure_code}</span>
                {failureAt ? (
                  <span className={s.linhaMeta}>
                    {t('overview.connection.row.lastFailure.at', { when: failureAt })}
                  </span>
                ) : null}
              </>
            ) : (
              <span className={s.vazio}>{t('overview.connection.row.lastFailure.none')}</span>
            )}
          </Row>

          {/* O rodapé diz de quando é a foto do `/debug/status`, a fonte de «Túnel» e «Raiz da CA do SPIRE»
              (aim-6ph.13, §1.2). As duas linhas de baixo já vêm do Postgres, pela sonda de contato, e têm hora própria. */}
          <ReadingNote
            reading={conexao.reading}
            pending={readingPending}
            compose={(reading) => t('overview.connection.readAt', { reading })}
            className={s.rodapeLeitura}
            testId="connection-reading"
          />
        </>
      )}
    </Card>
  );
}
